// src/pages/Campaigns/SurveyGenie/SurveyEmailWizard.js
import React, { useState, useEffect } from 'react';
import {
  Box, Stepper, Step, StepLabel, Button, Typography, Paper,
  TextField, Checkbox, List, ListItem, ListItemText, ListItemIcon,
  Alert, CircularProgress, Divider, Radio, RadioGroup, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Select, MenuItem, FormControl, InputLabel, Card, CardContent, Grid
} from '@mui/material';
import { CloudUpload, Mail, Delete, Add, Image as ImageIcon, Visibility, Download, Business, FilterList } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const steps = ['Select Customers', 'Connect Gmail', 'Compose', 'Review & Send'];

const SurveyEmailWizard = () => {
  const { chatbotId, surveyId } = useParams();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  
  // Data State
  const [panelists, setPanelists] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [selectionMode, setSelectionMode] = useState('existing');
  const [gmailConnected, setGmailConnected] = useState(false);

  // CRM State
  const [crmContacts, setCrmContacts] = useState([]);
  const [crmGroups, setCrmGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedCrmIds, setSelectedCrmIds] = useState([]);
  
  // Enhanced email content state
  const [emailContent, setEmailContent] = useState({
    subject: 'We want your feedback!',
    body: 'Hi {{name}},\n\nPlease take a moment to fill out our survey.\n\nThanks!',
    surveyTitle: '',
    surveyDescription: '',
    logoFile: null,
    logoUrl: '',
    disclaimer: '',
    selectedDisclaimerId: ''
  });
  
  //  Disclaimer Bank State
  const [disclaimerBank, setDisclaimerBank] = useState([]);
  const [disclaimerDialog, setDisclaimerDialog] = useState(false);
  const [newDisclaimer, setNewDisclaimer] = useState({ name: '', text: '' });
  const [previewDialog, setPreviewDialog] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    loadPanelists();
    checkGmail();
    loadDisclaimerBank();
    loadSurveyDetails();

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('gmail_connected') === 'true') {
      setGmailConnected(true);
      setActiveStep(2);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadPanelists = async () => {
    try {
      const res = await axiosInstance.get('/api/panelists', { params: { chatbot_id: chatbotId } });
      setPanelists(res.data.data || []);
    } catch (e) { console.error(e); }
  };

  const checkGmail = async () => {
    try {
      const res = await axiosInstance.get('/api/surveys/gmail/status', { params: { chatbot_id: chatbotId } });
      setGmailConnected(res.data.connected);
    } catch (e) { console.error(e); }
  };

  //  Load survey details
  const loadSurveyDetails = async () => {
    try {
      const res = await axiosInstance.get(`/api/surveys/${surveyId}`);
      if (res.data.success) {
        setEmailContent(prev => ({
          ...prev,
          surveyTitle: res.data.data.title || '',
          surveyDescription: res.data.data.description || ''
        }));
      }
    } catch (e) { console.error(e); }
  };

  //  Load disclaimer bank
  const loadDisclaimerBank = async () => {
    try {
      const res = await axiosInstance.get('/api/surveys/disclaimers', { 
        params: { chatbot_id: chatbotId } 
      });
      setDisclaimerBank(res.data.data || []);
    } catch (e) { console.error(e); }
  };

  //  Save disclaimer to bank
  const saveDisclaimer = async () => {
    if (!newDisclaimer.name || !newDisclaimer.text) {
      alert('Please fill in both name and text');
      return;
    }
    
    try {
      setLoading(true);
      await axiosInstance.post('/api/surveys/disclaimers', newDisclaimer, {
        params: { chatbot_id: chatbotId }
      });
      await loadDisclaimerBank();
      setDisclaimerDialog(false);
      setNewDisclaimer({ name: '', text: '' });
      alert('Disclaimer saved successfully!');
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to save disclaimer');
    } finally {
      setLoading(false);
    }
  };

  // Delete disclaimer
  const deleteDisclaimer = async (id) => {
    if (!window.confirm('Delete this disclaimer?')) return;
    
    try {
      await axiosInstance.delete('/api/surveys/disclaimers', {
        params: { disclaimer_id: id }
      });
      await loadDisclaimerBank();
    } catch (e) {
      alert('Failed to delete disclaimer');
    }
  };

  // Handle logo upload
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('File size must be less than 2MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploadingLogo(true);
      const res = await axiosInstance.post('/api/surveys/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { chatbot_id: chatbotId }
      });
      
      setEmailContent(prev => ({
        ...prev,
        logoFile: file,
        logoUrl: res.data.logo_url
      }));
      
      alert('Logo uploaded successfully!');
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Remove logo
  const removeLogo = () => {
    setEmailContent(prev => ({
      ...prev,
      logoFile: null,
      logoUrl: ''
    }));
  };

  // Download Sample CSV
  const handleDownloadSampleCSV = () => {
    const csvContent = `full_name,email,phone,company
John Doe,john.doe@example.com,+1234567890,Acme Corp
Jane Smith,jane.smith@example.com,+0987654321,Tech Solutions
Bob Johnson,bob.johnson@example.com,+1122334455,Global Industries`;
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'sample_customers.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleConnect = async () => {
    try {
      const response = await axiosInstance.get('/api/surveys/gmail/auth/start', {
        params: { 
          chatbot_id: chatbotId,
          survey_id: surveyId 
        }
      });
      
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      } else if (response.data.already_connected) {
        setGmailConnected(true);
        setActiveStep(2);
      }
    } catch (error) {
      console.error('Error initiating Gmail connection:', error);
      alert('Failed to connect to Gmail');
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    const formData = new FormData();
    formData.append('file', uploadFile);
    
    try {
      setLoading(true);
      await axiosInstance.post('/api/surveys/panelists/upload', formData, { 
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { chatbot_id: chatbotId } 
      });
      
      await loadPanelists();
      setUploadFile(null);
      setSelectionMode('existing');
      alert('Upload successful!');
    } catch (e) { 
      alert(e.response?.data?.message || 'Upload failed'); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleSend = async () => {
    try {
      setLoading(true);
      await axiosInstance.post('/api/surveys/distribute/email', {
        survey_id: surveyId,
        panelist_ids: selectionMode === 'existing' ? selectedIds : [], // Only send if mode is existing
        crm_contact_ids: selectionMode === 'crm' ? selectedCrmIds : [], // Send CRM IDs
        subject: emailContent.subject,
        body: emailContent.body,
        survey_title: emailContent.surveyTitle,
        survey_description: emailContent.surveyDescription,
        logo_url: emailContent.logoUrl,
        disclaimer: emailContent.disclaimer
      }, { params: { chatbot_id: chatbotId } });
      
      alert('Campaign Sent Successfully!');
      navigate(`/chatbot/${chatbotId}/surveys`);
    } catch (e) { 
      alert(e.response?.data?.message || 'Failed to send emails'); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
        if (selectionMode === 'existing' && selectedIds.length === 0) return alert('Select at least one customer');
        if (selectionMode === 'crm' && selectedCrmIds.length === 0) return alert('Select at least one CRM contact');
    }
    if (activeStep === 1 && !gmailConnected) return alert('Please connect Gmail first');
    setActiveStep((prev) => prev + 1);
  };

  const loadCrmContacts = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/api/crm/contacts', { params: { chatbot_id: chatbotId, per_page: 1000 } });
      const validContacts = (res.data.data || []).filter(c => c.email);
      setCrmContacts(validContacts);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadCrmGroups = async () => {
    try {
        const res = await axiosInstance.get('/api/crm/contact-groups', { params: { chatbot_id: chatbotId } });
        setCrmGroups(res.data.data || []);
    } catch (e) { console.error(e); }
  };

  // Trigger load when mode changes
  useEffect(() => {
      if (selectionMode === 'crm') {
          if (crmContacts.length === 0) loadCrmContacts();
          if (crmGroups.length === 0) loadCrmGroups();
      }
  }, [selectionMode]);

  // CRM Logic
  const filteredCrmContacts = selectedGroupId 
    ? crmContacts.filter(c => c.contact_group_id === selectedGroupId)
    : crmContacts;

  const isAllCrmSelected = filteredCrmContacts.length > 0 && filteredCrmContacts.every(c => selectedCrmIds.includes(c.id));

  const handleSelectAllCrm = (e) => {
    if (e.target.checked) {
        const visibleIds = filteredCrmContacts.map(c => c.id);
        setSelectedCrmIds(prev => [...new Set([...prev, ...visibleIds])]);
    } else {
        const visibleIds = filteredCrmContacts.map(c => c.id);
        setSelectedCrmIds(prev => prev.filter(id => !visibleIds.includes(id)));
    }
  };

  // --- Step Content Renderers ---

  const renderSelectStep = () => (
    <Box>
      <Box sx={{ mb: 2 }}>
        <RadioGroup row value={selectionMode} onChange={(e) => setSelectionMode(e.target.value)}>
          <FormControlLabel value="existing" control={<Radio />} label="Existing Panelists" />
          <FormControlLabel value="upload" control={<Radio />} label="Upload CSV/Excel" />
          <FormControlLabel value="crm" control={<Radio />} label="CRM Contacts" />
        </RadioGroup>
      </Box>

      {selectionMode === 'upload' ? (
        <Box sx={{ border: '1px dashed grey', p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Button variant="outlined" component="label" startIcon={<CloudUpload />}>
            Choose File
            <input type="file" hidden accept=".csv,.xlsx" onChange={(e) => setUploadFile(e.target.files[0])} />
          </Button>
          {uploadFile && <Typography sx={{ mt: 1 }}>{uploadFile.name}</Typography>}
          <Button variant="contained" onClick={handleUpload} disabled={!uploadFile || loading} sx={{ mt: 2, display: 'block', mx: 'auto' }}>
            {loading ? 'Uploading...' : 'Upload & Process'}
          </Button>
          
          <Divider sx={{ my: 2 }} />
          <Button 
            variant="text" 
            startIcon={<Download />} 
            onClick={handleDownloadSampleCSV}
            size="small"
            color="primary"
          >
            Download Sample CSV
          </Button>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
            Download a sample file to see the expected format
          </Typography>
        </Box>
      ) : selectionMode === 'crm' ? (
         // CRM UI
         <Box>
             <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-end">
                 <Grid item xs={12} md={6}>
                     <FormControl fullWidth size="small">
                         <InputLabel>Filter by Group</InputLabel>
                         <Select
                             value={selectedGroupId}
                             label="Filter by Group"
                             onChange={(e) => setSelectedGroupId(e.target.value)}
                         >
                             <MenuItem value=""><em>All Groups</em></MenuItem>
                             {crmGroups.map(g => (
                                 <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                             ))}
                         </Select>
                     </FormControl>
                 </Grid>
                 <Grid item xs={12} md={6}>
                     <FormControlLabel
                         control={
                             <Checkbox 
                                 checked={isAllCrmSelected}
                                 onChange={handleSelectAllCrm}
                                 disabled={filteredCrmContacts.length === 0}
                             />
                         }
                         label={`Select All Visible (${filteredCrmContacts.length})`}
                     />
                 </Grid>
             </Grid>

             <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
                {loading ? <Box p={3} textAlign="center"><CircularProgress /></Box> :
                 filteredCrmContacts.length === 0 ? (
                    <Box p={3} textAlign="center">
                      <Typography color="textSecondary">No CRM contacts found.</Typography>
                    </Box>
                 ) : (
                    <List>
                      {filteredCrmContacts.map((c) => (
                        <ListItem key={c.id} button onClick={() => {
                            setSelectedCrmIds(prev => prev.includes(c.id) ? prev.filter(id => id !== c.id) : [...prev, c.id]);
                        }}>
                          <ListItemIcon>
                            <Checkbox checked={selectedCrmIds.includes(c.id)} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={`${c.first_name} ${c.last_name || ''}`.trim()} 
                            secondary={c.email + (c.company_name ? ` • ${c.company_name}` : '')} 
                          />
                        </ListItem>
                      ))}
                    </List>
                 )}
             </Paper>
         </Box>
      ) : (
        <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
          {panelists.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography color="textSecondary">No customers found. Try uploading a CSV.</Typography>
            </Box>
          ) : (
            <List>
              {panelists.map((p) => (
                <ListItem key={p.id} button onClick={() => {
                  setSelectedIds(prev => prev.includes(p.id) ? prev.filter(id => id !== p.id) : [...prev, p.id]);
                }}>
                  <ListItemIcon>
                    <Checkbox checked={selectedIds.includes(p.id)} />
                  </ListItemIcon>
                  <ListItemText primary={p.full_name || p.email} secondary={p.email} />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}
      <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
        Selected: {selectionMode === 'crm' ? selectedCrmIds.length : selectedIds.length}
      </Typography>
    </Box>
  );

  const renderConnectStep = () => (
    <Box textAlign="center" py={4}>
      <Mail sx={{ fontSize: 60, color: gmailConnected ? 'success.main' : 'grey.400', mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        {gmailConnected ? 'Gmail Connected' : 'Connect your Gmail Account'}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        We use your Gmail account to send survey invitations personalized for each customer.
      </Typography>
      {!gmailConnected ? (
        <Button variant="contained" onClick={handleConnect}>
          Connect Gmail
        </Button>
      ) : (
        <Alert severity="success" sx={{ display: 'inline-flex' }}>Account Ready</Alert>
      )}
    </Box>
  );

  const renderComposeStep = () => (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <ImageIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Email Logo (Optional)
          </Typography>
          
          {emailContent.logoUrl ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
              <img 
                src={emailContent.logoUrl} 
                alt="Logo" 
                style={{ maxHeight: 80, maxWidth: 200, objectFit: 'contain' }}
              />
              <IconButton onClick={removeLogo} color="error">
                <Delete />
              </IconButton>
            </Box>
          ) : (
            <Button
              variant="outlined"
              component="label"
              startIcon={uploadingLogo ? <CircularProgress size={20} /> : <CloudUpload />}
              disabled={uploadingLogo}
              sx={{ mt: 2 }}
            >
              {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleLogoUpload}
              />
            </Button>
          )}
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
            Recommended: PNG or JPG, max 2MB
          </Typography>
        </CardContent>
      </Card>

      <TextField
        fullWidth
        label="Survey Title"
        value={emailContent.surveyTitle}
        onChange={(e) => setEmailContent({ ...emailContent, surveyTitle: e.target.value })}
        sx={{ mb: 3 }}
        helperText="Will be displayed prominently in the email"
      />

      <TextField
        fullWidth
        multiline
        rows={2}
        label="Survey Description"
        value={emailContent.surveyDescription}
        onChange={(e) => setEmailContent({ ...emailContent, surveyDescription: e.target.value })}
        sx={{ mb: 3 }}
        helperText="Brief description of the survey purpose"
      />

      <TextField 
        fullWidth 
        label="Email Subject" 
        sx={{ mb: 3 }}
        value={emailContent.subject}
        onChange={(e) => setEmailContent({ ...emailContent, subject: e.target.value })}
      />

      <TextField 
        fullWidth 
        multiline 
        rows={6} 
        label="Email Body"
        value={emailContent.body}
        onChange={(e) => setEmailContent({ ...emailContent, body: e.target.value })}
        helperText="Available variables: {{name}}, {{survey_link}}"
        sx={{ mb: 3 }}
      />

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Disclaimer (Optional)</Typography>
            <Button
              startIcon={<Add />}
              onClick={() => setDisclaimerDialog(true)}
              size="small"
            >
              Save to Bank
            </Button>
          </Box>

          {disclaimerBank.length > 0 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Choose from Bank</InputLabel>
              <Select
                value={emailContent.selectedDisclaimerId}
                label="Choose from Bank"
                onChange={(e) => {
                  const selected = disclaimerBank.find(d => d.id === e.target.value);
                  setEmailContent({
                    ...emailContent,
                    selectedDisclaimerId: e.target.value,
                    disclaimer: selected ? selected.text : ''
                  });
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {disclaimerBank.map((disc) => (
                  <MenuItem key={disc.id} value={disc.id}>
                    {disc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Disclaimer Text"
            value={emailContent.disclaimer}
            onChange={(e) => setEmailContent({ ...emailContent, disclaimer: e.target.value })}
            placeholder="This survey is confidential and your responses will be anonymous..."
          />
        </CardContent>
      </Card>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<Visibility />}
          onClick={() => setPreviewDialog(true)}
        >
          Preview Email
        </Button>
      </Box>

      <Dialog open={disclaimerDialog} onClose={() => setDisclaimerDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save Disclaimer to Bank</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Disclaimer Name"
            value={newDisclaimer.name}
            onChange={(e) => setNewDisclaimer({ ...newDisclaimer, name: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Disclaimer Text"
            value={newDisclaimer.text}
            onChange={(e) => setNewDisclaimer({ ...newDisclaimer, text: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisclaimerDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveDisclaimer} disabled={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Email Preview</DialogTitle>
        <DialogContent>
          <Box 
            dangerouslySetInnerHTML={{ 
              __html: generateEmailPreview() 
            }} 
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  const generateEmailPreview = () => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 600px; margin: 0 auto; background: #ffffff; }
          .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 40px 30px; text-align: center; }
          .logo { max-width: 150px; margin-bottom: 20px; }
          .content { padding: 40px 30px; }
          .survey-card { background: #f8f9fa; border-left: 4px solid #667eea; padding: 20px; margin: 25px 0; border-radius: 4px; }
          .cta-button { display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white !important; padding: 16px 40px; text-decoration: none; border-radius: 50px; font-weight: 600; margin: 20px 0; }
          .disclaimer { font-size: 12px; color: #666; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e0e0e0; }
          .footer { background: #f8f9fa; padding: 30px; text-align: center; color: #666; font-size: 14px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            ${emailContent.logoUrl ? `<img src="${emailContent.logoUrl}" alt="Logo" class="logo" />` : ''}
            <h1>${emailContent.surveyTitle || 'Survey Invitation'}</h1>
          </div>
          <div class="content">
            <p>Hello <strong>{{name}}</strong>,</p>
            ${emailContent.surveyDescription ? `<div class="survey-card"><p>${emailContent.surveyDescription}</p></div>` : ''}
            <div>${emailContent.body.replace(/\n/g, '<br>')}</div>
            <div style="text-align: center;">
              <a href="{{survey_link}}" class="cta-button">Take Survey</a>
            </div>
            ${emailContent.disclaimer ? `<div class="disclaimer">${emailContent.disclaimer.replace(/\n/g, '<br>')}</div>` : ''}
          </div>
          <div class="footer">
            <p>Thank you for your time!</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const renderReviewStep = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        You are about to send emails to <strong>{selectionMode === 'crm' ? selectedCrmIds.length : selectedIds.length}</strong> customers.
      </Alert>
      
      {emailContent.logoUrl && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Logo:</Typography>
          <img src={emailContent.logoUrl} alt="Logo" style={{ maxHeight: 60, marginTop: 8 }} />
        </Box>
      )}
      
      {emailContent.surveyTitle && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Survey Title:</Typography>
          <Typography>{emailContent.surveyTitle}</Typography>
        </Box>
      )}
      
      <Typography variant="subtitle2">Subject:</Typography>
      <Typography gutterBottom>{emailContent.subject}</Typography>
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="subtitle2">Body Preview:</Typography>
      <Paper sx={{ p: 2, bgcolor: 'grey.50', whiteSpace: 'pre-wrap', mb: 2 }}>
        {emailContent.body}
      </Paper>
      
      {emailContent.disclaimer && (
        <>
          <Typography variant="subtitle2">Disclaimer:</Typography>
          <Paper sx={{ p: 2, bgcolor: 'grey.50', fontSize: '0.875rem' }}>
            {emailContent.disclaimer}
          </Paper>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      <Typography variant="h4" fontWeight="bold" mb={4}>Send Survey Invitations</Typography>
      <Stepper activeStep={activeStep} sx={{ mb: 5 }}>
        {steps.map((label) => (<Step key={label}><StepLabel>{label}</StepLabel></Step>))}
      </Stepper>

      <Paper sx={{ p: 3, mb: 3 }}>
        {activeStep === 0 && renderSelectStep()}
        {activeStep === 1 && renderConnectStep()}
        {activeStep === 2 && renderComposeStep()}
        {activeStep === 3 && renderReviewStep()}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button onClick={() => activeStep === 0 ? navigate(-1) : setActiveStep(prev => prev - 1)}>
          Back
        </Button>
        {activeStep === steps.length - 1 ? (
          <Button variant="contained" onClick={handleSend} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Send Emails'}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleNext}>Next</Button>
        )}
      </Box>
    </Box>
  );
};

export default SurveyEmailWizard;